const { getElement } = window.BAO.Utils

/*
  With the keyboard up, iOS still lets a drag pan the visual viewport over a
  pinned page, and paints the caret of a field in a fixed sheet where the
  field was before the pan, so it floats off over the chat thread (#947,
  WebKit bug 176896). While a sheet's textarea has focus, a one-finger drag
  is cancelled unless it lands in a scroller, the field included, that can
  still move that way, which leaves the viewport nothing to pan with.
*/
let lastTouchY = 0

// A finger landing or lifting moves touches[0], so the next move is
// measured from wherever it now is.
function trackTouch (event) {
  if (event.touches.length) lastTouchY = event.touches[0].clientY
}

function onTouchMove (event) {
  const field = document.activeElement
  if (!field?.matches('.drw-Drawer-sheet.drw-Drawer-active textarea')) return

  const dy = event.touches[0].clientY - lastTouchY

  lastTouchY = event.touches[0].clientY

  if (event.touches.length > 1) return

  for (
    let el = event.target;
    el && el !== document.documentElement;
    el = el.parentElement
  ) {
    const canScroll =
      dy > 0
        ? el.scrollTop > 0
        : el.scrollTop + el.clientHeight < el.scrollHeight - 1
    if (canScroll && /auto|scroll/.test(getComputedStyle(el).overflowY)) return
  }

  event.preventDefault()
}

export class SiteDrawer extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.drawers = getElement('site-drawers')
    if (!this.drawers) {
      throw new Error(
        `${this.nodeName.toLowerCase()} needs a <site-drawers> element to work`,
      )
    }
    this.drawers.drawers.push(this)

    this._isOpen = false
  }

  connectedCallback () {
    super.connectedCallback()

    if (this.drawerShouldBeMoved) {
      window.setTimeout(() => {
        this.moveToElement.appendChild(this)
      }, parseInt(this.getAttribute('data-module-drawers-move-delay'), 10) || 0)
    }

    this.addEventListener('scroll', this.onScroll.bind(this), {
      passive: true,
    })
  }

  get key () {
    // `key` is a reserved prop in JSX renderers, which cannot emit it as a
    // plain attribute — data-key is the server-rendered equivalent.
    return this.getAttribute('key') || this.getAttribute('data-key')
  }

  get elements () {
    return {
      trigger: getElement(`[data-drawers-trigger=${this.key}]`),
      close: getElement(`[data-module-drawers-close=${this.key}]`),
    }
  }

  get isOpen () {
    return this._isOpen
  }

  set isOpen (val) {
    this._isOpen = val

    this.classList.toggle(this.config.classes.activeDrawerClass, val)
    this.pinPage(val)
  }

  /*
    iOS scrolls the page behind an open sheet whatever `overflow: hidden`
    says, so scrolling the chat thread carried the list with it and closing
    the chat left it at the bottom (#907). While a sheet is open the page is
    pinned where it was (`util-SheetOpen` in critical-head-styles.liquid) and
    put back there on close, and the drag guard above (#947) is on.
  */
  pinPage (on) {
    if (!this.classList.contains('drw-Drawer-sheet')) return

    this.pinsPage = on

    // site-drawers sets isOpen on every drawer, in no particular order, so
    // one sheet can open before or after another closes: the page stays
    // pinned while any sheet is open, from the scroll it had before the first.
    const pinned = this.drawers.drawers.some(drawer => drawer.pinsPage)
    const root = document.documentElement

    if (pinned === root.classList.contains('util-SheetOpen')) return

    const method = pinned ? 'addEventListener' : 'removeEventListener'

    document[method]('touchstart', trackTouch, { passive: true })
    document[method]('touchend', trackTouch, { passive: true })
    document[method]('touchmove', onTouchMove, { passive: false })

    if (pinned) {
      this.drawers.pageScroll = window.scrollY
      root.style.setProperty('--SheetOpen_Top', `${-window.scrollY}px`)
      root.classList.add('util-SheetOpen')
    } else {
      root.classList.remove('util-SheetOpen')
      window.scrollTo({ top: this.drawers.pageScroll, behavior: 'instant' })
    }
  }

  get drawerShouldBeMoved () {
    return this.hasAttribute('data-module-drawers-move-me')
  }

  get moveToElement () {
    switch (this.getAttribute('data-module-drawers-move-me')) {
      case 'root':
        return this.drawers
      default:
        return getElement(this.getAttribute('data-module-drawers-move-me'))
    }
  }

  get config () {
    return {
      classes: {
        activeDrawerClass: 'drw-Drawer-active',
      },
    }
  }

  onScroll = event => {
    const drawersEl = event.target

    const scrollTop = drawersEl.scrollTop

    this.toggleAttribute('scrollstart', scrollTop <= 40)
    this.toggleAttribute('scrollend', scrollTop >= 40)
  }
}

customElements.define('site-drawer', SiteDrawer)
